import { Reducer } from 'redux';
import { isEmpty, remove, cloneDeep } from 'lodash-es';

import { Template } from '@dev/ee-api/ee-api-models';
import {
    RawEditorState,
    AddTemplateTagsAction,
    RemoveTemplateTagsAction,
    ModifyTemplateNameSuccessAction,
    RawEditorActionTypes,
    ModifyTextBodyAction,
    ModifyAMPBodyAction,
    ChangeSubjectAction,
    ChangePreviewTextAction,
    ChangeFromAction,
    ChangeFromNameAction,
    ChangeDefaultOptionsAction,
    ChangeToAction,
    GetDefaultTemplateStartAction,
    GetDefaultTemplateSuccessAction,
    ChangeTemplateBodyAction,
    FetchExistingTemplateErrorAction,
    FetchExistingTemplateSuccessAction,
    LoadTemplateAction,
    SavePastBodyAction,
    SaveLastScreenshotAction,
    SetTemplateFromGalleryToLoadAction,
} from './types';

const PAST_LIMIT = 10;

const defaultState: RawEditorState = {
    templateLoading: false,
    templateSaving: false,
    templateNameSaving: false,
    changesSaved: null,
    currentBody: null,
    pastBody: [],
    futureBody: [],
    isNewTemplate: null,
};

const getTemplateObject = (template: string, state: RawEditorState): Template => {
    const { templateObject = {} } = state;
    const clonedTemplates = cloneDeep(templateObject);

    const updatedMimeObj = { ContentType: 'text/html', Content: template };
    const { Parts } = templateObject?.Body || {};
    const clonedParts = cloneDeep(Parts);

    if (isEmpty(Parts)) {
        clonedTemplates.Body.Parts = [updatedMimeObj];
    } else {
        clonedTemplates.Body.Parts = remove(clonedParts, (o) => {
            o.ContentType !== 'text/html';
        });
        clonedTemplates?.Body?.Parts?.push(updatedMimeObj);
    }

    return clonedTemplates;
};

const saveChangesStart = (state: RawEditorState): RawEditorState => ({
    ...state,
    templateSaving: true,
});

const saveChangesSuccess = (state: RawEditorState): RawEditorState => ({
    ...state,
    changesSaved: true,
    templateSaving: false,
    templateNameSaving: false,
    isNewTemplate: false,
});

const saveChangesError = (state: RawEditorState): RawEditorState => ({
    ...state,
    changesSaved: null,
    templateSaving: false,
    templateNameSaving: false,
});

const fetchSuccess = (state: RawEditorState, action: FetchExistingTemplateSuccessAction): RawEditorState => {
    const { isNewTemplate, currentBody, templateObject } = action.payload;
    return {
        ...state,
        templateLoading: false,
        isNewTemplate,
        currentBody,
        templateObject,
        templateName: templateObject.Name,
    };
};

const fetchStart = (state: RawEditorState): RawEditorState => ({
    ...state,
    templateLoading: true,
    changesSaved: null,
});

const modifyTo = (state: RawEditorState, action: ChangeToAction): RawEditorState => {
    const { sendTo } = action.payload;
    return {
        ...state,
        sendTo,
    };
};

const resetSaving = (state: RawEditorState): RawEditorState => ({
    ...state,
    templateSaving: false,
    templateNameSaving: false,
});

const addTag = (state: RawEditorState, action: AddTemplateTagsAction): RawEditorState => {
    const { tag } = action.payload;
    const clonedTemplateObject = cloneDeep(state.templateObject);
    const Tags = clonedTemplateObject?.Tags ? [...clonedTemplateObject.Tags, tag] : [tag];
    return {
        ...state,
        changesSaved: false,
        templateObject: { ...clonedTemplateObject, Tags },
    };
};

const removeTag = (state: RawEditorState, action: RemoveTemplateTagsAction): RawEditorState => {
    const { tag } = action.payload;
    const clonedTemplateObject = cloneDeep(state.templateObject);
    const Tags = state.templateObject?.Tags?.filter((o) => o !== tag) ?? [];

    return {
        ...state,
        changesSaved: false,
        templateObject: { ...clonedTemplateObject, Tags },
    };
};

const modifyTextBody = (state: RawEditorState, action: ModifyTextBodyAction): RawEditorState => {
    const { textBody } = action.payload;
    const clonedTemplateObject = cloneDeep(state.templateObject);
    const Parts = clonedTemplateObject.Body.Parts.filter((o) => o.ContentType !== 'text/plain');
    Parts.push({
        ContentType: 'text/plain',
        Content: textBody,
    });
    clonedTemplateObject.Body.Parts = Parts;
    return {
        ...state,
        templateObject: clonedTemplateObject,
        changesSaved: false,
    };
};

const modifyAMPBody = (state: RawEditorState, action: ModifyAMPBodyAction): RawEditorState => {
    const { ampBody } = action.payload;
    const clonedTemplateObject = cloneDeep(state.templateObject);
    const Parts = clonedTemplateObject.Body.Parts.filter((o) => o.ContentType !== 'text/x-amp-html');
    Parts.push({
        ContentType: 'text/x-amp-html',
        Content: ampBody,
    });
    clonedTemplateObject.Body.Parts = Parts;
    return {
        ...state,
        templateObject: clonedTemplateObject,
        changesSaved: false,
    };
};

const modifySubject = (state: RawEditorState, action: ChangeSubjectAction): RawEditorState => {
    const { subject } = action.payload;
    const clonedTemplateObject = cloneDeep(state.templateObject);
    clonedTemplateObject.Body.Subject = subject;
    return {
        ...state,
        templateObject: clonedTemplateObject,
        changesSaved: false,
    };
};

const modifyPreviewText = (state: RawEditorState, action: ChangePreviewTextAction): RawEditorState => {
    const { previewText } = action.payload;
    const clonedTemplateObject = cloneDeep(state.templateObject);
    clonedTemplateObject.Body.PreHeader = previewText;
    return {
        ...state,
        templateObject: clonedTemplateObject,
        changesSaved: false,
    };
};

const modifyFrom = (state: RawEditorState, action: ChangeFromAction): RawEditorState => {
    const { fromEmail } = action.payload;
    const clonedTemplateObject = cloneDeep(state.templateObject);
    clonedTemplateObject.FromEmail = fromEmail;
    return {
        ...state,
        templateObject: clonedTemplateObject,
        changesSaved: false,
    };
};

const modifyFromName = (state: RawEditorState, action: ChangeFromNameAction): RawEditorState => {
    const { fromName } = action.payload;
    const clonedTemplateObject = cloneDeep(state.templateObject);
    clonedTemplateObject.FromName = fromName;
    return {
        ...state,
        templateObject: clonedTemplateObject,
        changesSaved: false,
    };
};

const changeDefaultOptions = (state: RawEditorState, action: ChangeDefaultOptionsAction): RawEditorState => {
    const { params } = action.payload;
    const clonedTemplateObject = cloneDeep(state.templateObject);

    clonedTemplateObject.FromName = params.fromName;
    clonedTemplateObject.FromEmail = params.fromEmail;
    clonedTemplateObject.Body.Subject = params.subject;

    return {
        ...state,
        templateObject: clonedTemplateObject,
        changesSaved: false,
    };
};

const changeTemplateName = (state: RawEditorState): RawEditorState => ({
    ...state,
    changesSaved: false,
    templateNameSaving: true,
});

const changeTemplateNameSuccess = (state: RawEditorState, action: ModifyTemplateNameSuccessAction): RawEditorState => {
    const clonedTemplateObject = cloneDeep(state.templateObject);
    const name = action.payload.name;
    return {
        ...state,
        templateName: name,
        templateObject: { ...clonedTemplateObject, Name: name },
    };
};

const getDefaultTemplateStart = (state: RawEditorState, action: GetDefaultTemplateStartAction): RawEditorState => {
    const { templateType } = action.payload;
    return {
        ...state,
        templateType,
    };
};

const getDefaultTemplateSuccess = (state: RawEditorState, action: GetDefaultTemplateSuccessAction): RawEditorState => {
    const { template, isNewTemplate } = action.payload;
    return {
        ...state,
        template,
        isNewTemplate,
    };
};

const changeTemplateBody = (state: RawEditorState, action: ChangeTemplateBodyAction): RawEditorState => {
    const { currentBody } = action.payload;
    return {
        ...state,
        currentBody,
    };
};

const fetchExistingTemplateError = (
    state: RawEditorState,
    action: FetchExistingTemplateErrorAction
): RawEditorState => {
    const { templateLoading, currentBody, templateObject } = action.payload;
    return {
        ...state,
        templateLoading,
        currentBody,
        templateObject,
    };
};

const loadTemplate = (state: RawEditorState, action: LoadTemplateAction): RawEditorState => {
    const { templateName, isNewTemplate, isPublic } = action.payload;
    return {
        ...state,
        templateName,
        isNewTemplate,
        isPublic,
    };
};

const redoBody = (state: RawEditorState): RawEditorState => {
    if (state.futureBody.length < 1) {
        return state;
    }

    const { currentBody, pastBody, futureBody } = state;

    const next = futureBody[0];
    const newFuture = futureBody.slice(1);

    return {
        ...state,
        changesSaved: false,
        pastBody: [currentBody, ...pastBody],
        currentBody: next,
        futureBody: newFuture,
        templateObject: getTemplateObject(next, state),
    };
};

const undoBody = (state: RawEditorState): RawEditorState => {
    if (state.pastBody.length < 1) {
        return state;
    }
    const { currentBody, pastBody, futureBody } = state;

    const previous = pastBody[0];
    const newPast = pastBody.slice(1);

    return {
        ...state,
        changesSaved: false,
        pastBody: newPast,
        currentBody: previous,
        futureBody: [currentBody, ...futureBody],
        templateObject: getTemplateObject(previous, state),
    };
};

const savePastBody = (state: RawEditorState, action: SavePastBodyAction): RawEditorState => {
    const { payload } = action;
    return {
        ...state,
        pastBody:
            (payload.pastBody ? [payload.pastBody, ...state.pastBody] : state.pastBody).length > PAST_LIMIT
                ? (payload.pastBody ? [payload.pastBody, ...state.pastBody] : state.pastBody).slice(0, PAST_LIMIT)
                : payload.pastBody
                ? [payload.pastBody, ...state.pastBody]
                : state.pastBody,
        futureBody: [],
        templateObject: getTemplateObject(state.currentBody, state),
        changesSaved: false,
    };
};

const saveLastScreenshot = (state: RawEditorState, action: SaveLastScreenshotAction): RawEditorState => {
    const { lastScreenshot } = action.payload;
    return {
        ...state,
        lastScreenshot,
    };
};

const openEditorInCampaignCreator = (state: RawEditorState): RawEditorState => ({
    ...state,
    isCampaignCreator: true,
});

const closeEditorInCampaignCreator = (state: RawEditorState): RawEditorState => ({
    ...state,
    isCampaignCreator: false,
});

const setTemplateFromGalleryToLoad = (state: RawEditorState, action: SetTemplateFromGalleryToLoadAction) => {
    const { templateName, isNew, isPublic } = action.payload;
    return {
        ...state,
        templateName,
        isPublic,
        isNewTemplate: isNew,
        isTemplateFromGallery: true,
    };
};

const getDefaultState = (): RawEditorState => ({
    ...defaultState,
});

const reducer: Reducer<RawEditorState> = (state: RawEditorState = defaultState, action): RawEditorState => {
    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (action.type) {
        case RawEditorActionTypes.SAVE_CHANGES_START:
            return saveChangesStart(state);
        case RawEditorActionTypes.SAVE_CHANGES_SUCCESS:
            return saveChangesSuccess(state);
        case RawEditorActionTypes.SAVE_CHANGES_ERROR:
            return saveChangesError(state);
        case RawEditorActionTypes.FETCH_SUCCESS:
            return fetchSuccess(state, action as FetchExistingTemplateSuccessAction);
        case RawEditorActionTypes.FETCH_START:
            return fetchStart(state);
        case RawEditorActionTypes.MODIFY_TO:
            return modifyTo(state, action as ChangeToAction);
        case RawEditorActionTypes.SEND_TEST_EMAIL:
        case RawEditorActionTypes.SAVE_CHANGES_AFTER_CLOSE_EDITOR_SUCCESS:
            return resetSaving(state);
        case RawEditorActionTypes.ADD_TAG:
            return addTag(state, action as AddTemplateTagsAction);
        case RawEditorActionTypes.REMOVE_TAG:
            return removeTag(state, action as RemoveTemplateTagsAction);
        case RawEditorActionTypes.MODIFY_TEXT_BODY:
            return modifyTextBody(state, action as ModifyTextBodyAction);
        case RawEditorActionTypes.MODIFY_AMP_BODY:
            return modifyAMPBody(state, action as ModifyAMPBodyAction);
        case RawEditorActionTypes.MODIFY_SUBJECT:
            return modifySubject(state, action as ChangeSubjectAction);
        case RawEditorActionTypes.MODIFY_PREVIEW_TEXT:
            return modifyPreviewText(state, action as ChangePreviewTextAction);
        case RawEditorActionTypes.MODIFY_FROM:
            return modifyFrom(state, action as ChangeFromAction);
        case RawEditorActionTypes.MODIFY_FROM_NAME:
            return modifyFromName(state, action as ChangeFromNameAction);
        case RawEditorActionTypes.CHANGE_DEFAULT_OPTIONS:
            return changeDefaultOptions(state, action as ChangeDefaultOptionsAction);
        case RawEditorActionTypes.CHANGE_TEMPLATE_NAME:
            return changeTemplateName(state);
        case RawEditorActionTypes.CHANGE_TEMPLATE_NAME_SUCCESS:
            return changeTemplateNameSuccess(state, action as ModifyTemplateNameSuccessAction);
        case RawEditorActionTypes.GET_DEFAULT_TEMPLATE_START:
            return getDefaultTemplateStart(state, action as GetDefaultTemplateStartAction);
        case RawEditorActionTypes.GET_DEFAULT_TEMPLATE_SUCCESS:
            return getDefaultTemplateSuccess(state, action as GetDefaultTemplateSuccessAction);
        case RawEditorActionTypes.CHANGE_TEMPLATE_BODY:
            return changeTemplateBody(state, action as ChangeTemplateBodyAction);
        case RawEditorActionTypes.FETCH_ERROR:
            return fetchExistingTemplateError(state, action as FetchExistingTemplateErrorAction);
        case RawEditorActionTypes.LOAD_TEMPLATE:
            return loadTemplate(state, action as LoadTemplateAction);
        case RawEditorActionTypes.REDO_CHANGES:
            return redoBody(state);
        case RawEditorActionTypes.UNDO_CHANGES:
            return undoBody(state);
        case RawEditorActionTypes.SAVE_PAST_BODY:
            return savePastBody(state, action as SavePastBodyAction);
        case RawEditorActionTypes.SAVE_LAST_SCREENSHOT:
            return saveLastScreenshot(state, action as SaveLastScreenshotAction);
        case RawEditorActionTypes.OPEN_EDITOR_IN_CAMPAIGN_CREATOR:
            return openEditorInCampaignCreator(state);
        case RawEditorActionTypes.CLOSE_EDITOR_IN_CAMPAIGN_CREATOR:
            return closeEditorInCampaignCreator(state);
        case RawEditorActionTypes.SET_TEMPLATE_FROM_GALLERY_TO_LOAD:
            return setTemplateFromGalleryToLoad(state, action as SetTemplateFromGalleryToLoadAction);
        case RawEditorActionTypes.CLOSE_EDITOR_SUCCESS:
            return getDefaultState();
        case RawEditorActionTypes.GET_DEFAULT_TEMPLATE_ERROR:
        default:
            return state;
    }
};

export { reducer as rawEditor };

export default reducer;
